var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getErrorMessage } from '../utils';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let globalConfig = {};
let reloadConfigFunction = undefined;
/**
 * Retrieves the global configuration object cast to the provided type.
 *
 * @template E - The expected type of the configuration object.
 * @returns {E} The global configuration object.
 */
export function getConfig() {
    return globalConfig;
}
/**
 * Resets the global configuration object to an empty state.
 *
 * @returns {Record<string, any>} The cleared global configuration object.
 */
export function resetConfig() {
    globalConfig = {};
    return globalConfig;
}
/**
 * Calls the reload function for the global configuration, if defined.
 *
 * @returns {Promise<void>} A promise that resolves once reloading is complete.
 */
export function reloadConfig() {
    if (reloadConfigFunction) {
        return reloadConfigFunction === null || reloadConfigFunction === void 0 ? void 0 : reloadConfigFunction();
    }
    else {
        return Promise.resolve();
    }
}
function combineErrors(params) {
    let message = '';
    params.forEach((param) => {
        if (param.error) {
            message += getErrorMessage(param.error);
        }
    });
    return message;
}
function combineData(params, namespace) {
    if (namespace && !globalConfig[namespace]) {
        globalConfig[namespace] = {};
    }
    params.forEach((param) => {
        if (param.data) {
            if (param.key && namespace) {
                globalConfig[namespace][param.key] = Object.assign(Object.assign({}, globalConfig[namespace]), param.data);
            }
            else if (!param.key && namespace) {
                globalConfig[namespace] = Object.assign(Object.assign({}, globalConfig[namespace]), param.data);
            }
            else if (param.key && !namespace) {
                globalConfig[param.key] = Object.assign({}, param.data);
            }
            else {
                globalConfig = Object.assign(Object.assign({}, globalConfig), param.data);
            }
        }
    });
    return useConfig;
}
/**
 * React hook that merges multiple configuration sources into a single config object.
 * Provides utility metadata such as loading state, errors, and reload support.
 *
 * @template E - The expected shape of the merged configuration data.
 * @param {UseConfigParams[]} params - Array of configuration sources to merge.
 * @param {string} [namespace] - Optional namespace for organizing the config.
 * @param {boolean} [finalConfig] - If true, sets this hook as the reloadable config.
 * @returns {{
 *   data: E;
 *   isLoading: boolean;
 *   error?: Error;
 *   reload: () => Promise<void>;
 * }} An object containing the merged configuration, loading state, error, and reload function.
 */
export function useConfig(params, namespace, finalConfig) {
    const errors = combineErrors(params);
    const isLoading = params.some((param) => param.isLoading);
    const reload = () => __awaiter(this, void 0, void 0, function* () {
        yield Promise.all(params.map((param) => {
            if (param.reload) {
                return param.reload();
            }
        }));
    });
    if (finalConfig) {
        reloadConfigFunction = () => reload();
    }
    if (params.length < 1) {
        throw new Error('At least one config is required');
    }
    return {
        data: combineData(params, namespace),
        isLoading: isLoading,
        error: errors ? new Error(errors) : undefined,
        reload,
    };
}
